import * as React from 'react';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { Kunde, useKundeList } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { cloneDeep, isEqual } from 'lodash';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import classnames from 'classnames';
import {
    Zusatzadresse,
    addZusatzadresse,
    defaultZusatzadresse,
    updateZusatzadresse,
    useZusatzadresseList,
    ZusatzadresseTyp,
} from 'api/zusatzadresse';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import { LandByLkz2 } from 'formComponents/lookups/LaenderByIkz2';
import { ZusatzadresseTypSelect } from 'formComponents/lookups/ZusatzadresseTypSelect';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { logDifference } from 'utils/helper';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';

const styles = mergeStyleSets({
    archiv: {
        flexDirection: 'row-reverse',
    },
});

interface ZusatzadresseEditProps {
    kNr: number;
    typ: string;
    zaNr: number; // -1 = new
    onClose: (refetch: boolean) => void;
}

export const ZusatzadresseEdit: React.FC<ZusatzadresseEditProps> = ({ kNr, typ, zaNr, onClose }) => {
    const auth = useSelector(authSelector);
    const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const isMounted = useIsMounted();

    const zusatzadresseList = useZusatzadresseList(
        {
            kNr,
            typ,
            nr: zaNr,
            includeArchiv: true,
        },
        { enabled: false },
    );

    const [zusatzadresse, setZusatzadresse] = React.useState<Zusatzadresse | null>(null);

    const kundeList = useKundeList({
        nr: kNr,
    });

    const [kunde, setKunde] = React.useState<Kunde | null>(null);

    React.useEffect(() => {
        if (!kundeList.isLoading && !kundeList.isFetching && kundeList.data?.length === 1 && kunde === null) {
            setKunde(kundeList.data[0]);
            zusatzadresseList.refetch();
        }
    }, [kunde, kundeList, zusatzadresseList]);

    React.useEffect(() => {
        if (
            !zusatzadresseList.isLoading &&
            !zusatzadresseList.isFetching &&
            zusatzadresseList.isSuccess &&
            zusatzadresse === null
        ) {
            if (zusatzadresseList.data?.length === 1) {
                setZusatzadresse(cloneDeep(zusatzadresseList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                setZusatzadresse({
                    ...defaultZusatzadresse,
                    name1: kunde?.name1 || '',
                    name2: kunde?.name2 || '',
                    land: kunde?.land || '',
                    plz: kunde?.plz || '',
                    ort: kunde?.ort || '',
                    strasse: kunde?.strasse || '',
                    kNr,
                });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [zusatzadresse, zusatzadresseList, kunde, kNr, auth]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !zusatzadresseList.isLoading &&
            !zusatzadresseList.isFetching &&
            zusatzadresseList.data?.length === 1 &&
            zusatzadresse !== null &&
            !isEqual(zusatzadresseList.data[0], zusatzadresse)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('zusatzadresse', zusatzadresse, zusatzadresseList.data[0]);
        }
    }, [savingState, zusatzadresseList, zusatzadresse]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const save = () => {
        if (zusatzadresse && savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            if (zaNr === -1) {
                addZusatzadresse(zusatzadresse)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }
                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            } else {
                updateZusatzadresse(zusatzadresse)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }
                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            }
        }
    };

    const renderTextField = (column: keyof Zusatzadresse, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(zusatzadresse?.[column] || '') as string}
            savingState={savingState}
            options={options}
            onChange={(newValue): void => {
                if (zusatzadresse) {
                    setZusatzadresse({
                        ...zusatzadresse,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderCheckbox = (column: keyof Zusatzadresse, label: string) => (
        <Checkbox
            className={classnames(formStyles.checkbox, { [styles.archiv]: column === 'archiv' })}
            label={label}
            disabled={savingState === SavingState.saving}
            checked={zusatzadresse?.[column] as boolean}
            onChange={(event, checked) => {
                if (zusatzadresse) {
                    setZusatzadresse({
                        ...zusatzadresse,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderZusatzadresseTyp = () => (
        <ZusatzadresseTypSelect
            value={zusatzadresse?.typ || 'L'}
            disabled={savingState === SavingState.saving || zaNr !== -1}
            menuPlacement="top"
            onChange={(newValue: ZusatzadresseTyp): void => {
                if (zusatzadresse) {
                    setZusatzadresse({
                        ...zusatzadresse,
                        typ: newValue,
                    });
                }
            }}
        />
    );

    const renderLand = () => (
        <LandByLkz2
            value={zusatzadresse?.land || 'AT'}
            disabled={savingState === SavingState.saving}
            onChange={(newValue): void => {
                if (zusatzadresse) {
                    setZusatzadresse({
                        ...zusatzadresse,
                        land: newValue,
                    });
                }
            }}
        />
    );

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={`Zusatzadresse bei ${kunde?.name1 || '...'} ${kunde?.name2 || ''}`}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth={800}
                onDismiss={() => {
                    onClose(false);
                }}
            >
                {zusatzadresseList.isLoading ||
                zusatzadresseList.isFetching ||
                kundeList.isLoading ||
                kundeList.isFetching ||
                !zusatzadresse ||
                !kunde ? (
                    <LoadingIndicator />
                ) : (
                    <form className={formStyles.form}>
                        <div className={formStyles.threeColumnsBlock}>
                            <div className={formStyles.gridCell}>{renderZusatzadresseTyp()}</div>
                            <div className={formStyles.checkboxWithoutLabel}>{renderCheckbox('std', 'Standard')}</div>
                            <div className={formStyles.checkboxWithoutLabel}>{renderCheckbox('archiv', 'Archiv')}</div>
                        </div>

                        <div className={formStyles.twoColumnsBlock}>
                            <div className={formStyles.gridCell}>
                                {renderTextField('name1', 'Name 1', { maxLength: 50 })}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('name2', 'Name 2', { maxLength: 50 })}
                            </div>
                        </div>

                        <div className={formStyles.gridAddressWithoutPostfach}>
                            <div className={formStyles.gridCell}>{renderLand()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('plz', 'PLZ', { maxLength: 10 })}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('ort', 'Ort', { maxLength: 50 })}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('strasse', 'Strasse', { maxLength: 50 })}
                            </div>
                        </div>

                        <Timestamps
                            modifiedDate={zusatzadresse.updDat}
                            modifiedDecimalTime={zusatzadresse.updUhr}
                            modifiedUser={zusatzadresse.updUser}
                            showCreated={false}
                        />

                        {savingState === SavingState.error && (
                            <div className={formStyles.error}>Fehler beim Speichern.</div>
                        )}
                        <DialogFooter>
                            {savingState === SavingState.saving ? (
                                <PrimaryButton text="Speichere..." disabled />
                            ) : (
                                <PrimaryButton
                                    onClick={() => {
                                        if (zaNr === -1) {
                                            save();
                                        } else {
                                            setShowConfirmDialog(true);
                                        }
                                    }}
                                    text="Speichern"
                                    disabled={savingState === SavingState.notChanged || zusatzadresseList.isLoading}
                                />
                            )}
                        </DialogFooter>
                    </form>
                )}
            </Dialog>
            {showConfirmDialog && (
                <ConfirmMessage
                    title="Soll die Änderung an der Zusatzadresse gespeichert werden?"
                    message={
                        'Die Änderung wird auf sämtlichen Aufträgen/Lieferscheinen/..., ' +
                        'die diese Adresse nutzen, sichtbar.'
                    }
                    onConfirm={() => {
                        save();
                    }}
                    onCancel={() => setShowConfirmDialog(false)}
                />
            )}
        </>
    );
};
